import React from 'react';
import { FlatList } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import styled from 'styled-components/native';

import PrimeBookCard from '@src/components/PrimeBookCard';
import Separator from '@src/components/Separator';
import Title from '@src/components/Title';
import { idKeyExtractor, padding } from '@src/configuration/constants';
import useLanguage from '@src/hooks/useLanguage';
import usePrimeBooks from '@src/hooks/usePrimeBooks';
import { PageItemAPI, PrimeBook } from '@src/types';

type Props = {
  data: PageItemAPI;
  onLongPress?: (primeId: string) => void;
};

const height = 350;

const Container = styled.View`
  width: 100%;
  height: ${height}px;
`;

const List = styled(FlatList as new () => FlatList<PrimeBook>).attrs(() => ({
  horizontal: true,
  initialNumToRender: 10,
  keyExtractor: idKeyExtractor,
  showsHorizontalScrollIndicator: false,
  ItemSeparatorComponent: Separator,
  contentContainerStyle: {
    paddingHorizontal: padding,
  },
}))``;

const Component: React.FC<Props> = (props) => {
  const {
    data: { name, url, params },
    onLongPress,
  } = props;

  const language = useLanguage();
  const books = usePrimeBooks(url, params);
  const navigation = useNavigation();

  const title = React.useMemo(() => name?.[language] ?? '', [language, name]);

  const handleViewMorePress = React.useCallback(() => {
    navigation.navigate('PrimeBooks', { title, url, params });
  }, [navigation, params, title, url]);

  return (
    <Container>
      <Separator />
      <Title onMorePress={handleViewMorePress}>{title}</Title>
      <List
        data={books.data}
        renderItem={({ item }) => (
          <PrimeBookCard
            item={item}
            onLongPress={() => onLongPress?.(item.id)}
          />
        )}
      />
    </Container>
  );
};

Component.displayName = 'Books';

export default React.memo(Component);
